import { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import {
  useBreakpointHelpers,
  useNoticeBannersStackContext,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';
import { getUrqlError } from '@order/utils';

import { useSubmitPromoCodeMutation } from '../GraphQL/PromoCode.generated';
import { usePromoCodeTelemetry } from './usePromoCode.telemetry';

export const usePromoCode = (props?: UsePromoCodeProps) => {
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { t } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();
  const navigation = useNavigation();
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);

  const [submitPromoCodeResponse, submitPromoCodeMutation] =
    useSubmitPromoCodeMutation();
  const { fetching: isSubmittingPromoCode } = submitPromoCodeResponse;

  const { onMonitoredPromoCodeApplied, onMonitoredPromoCodeFailed } =
    usePromoCodeTelemetry();

  const handleSetPromoCode = useCallback((input: string) => {
    setPromoCode(input);
    setPromoCodeInvalid(false);
  }, []);

  const submitPromoCode = useCallback(async () => {
    const response = await submitPromoCodeMutation({
      input: { code: promoCode },
    });

    const typename = response.data?.submitPromoCode?.__typename;
    const isSubmitSuccess = typename === 'SubmitPromoCodeSuccess';
    const isInvalidPromoCode = typename === 'InvalidPromoCode';

    setPromoCodeInvalid(isInvalidPromoCode);

    if (isInvalidPromoCode) {
      onMonitoredPromoCodeFailed(
        t('account.notice.credit-invalid'),
        getUrqlError(response.error),
      );
    }

    if (isSubmitSuccess) {
      setPromoCode('');
      onMonitoredPromoCodeApplied();

      if (isMobile && navigation.canGoBack()) {
        navigation.goBack();
      }

      addNoticeBanner({
        text: t('account.notice.credit-applied'),
        palette: 'success',
      });

      props?.refetchCredits?.();
    }
  }, [
    props,
    promoCode,
    isMobile,
    navigation,
    t,
    addNoticeBanner,
    submitPromoCodeMutation,
    onMonitoredPromoCodeFailed,
    onMonitoredPromoCodeApplied,
  ]);

  return {
    promoCode,
    isSubmittingPromoCode,
    promoCodeInvalid,
    setPromoCode: handleSetPromoCode,
    submitPromoCode,
  };
};

type UsePromoCodeProps = {
  readonly refetchCredits?: () => void;
};
